import { TextField, Box, Typography, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, { useState, useEffect } from 'react';
import { getListForSqlIn } from '../helpers/functions';
import { makeStyles } from '@material-ui/core/styles';
import SelectAllRoundedIcon from '@material-ui/icons/SelectAllRounded';
import ClearAllRoundedIcon from '@material-ui/icons/ClearAllRounded';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => ({
    filterSelectContainer: {
        display: 'flex',
        flexDirection: 'column',
        // MAINTIEN DE LA LARGEUR FIXE
        flexShrink: 0,
        flexGrow: 0,
    },
    autocompleteRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        width: '100%',
        marginBottom: theme.spacing(0.5),
    },
    autocompleteWrapper: {
        flexGrow: 1,
        minWidth: 0,
        // Gérer le débordement du texte dans le TextField
        '& .MuiOutlinedInput-root': {
            // Assurez-vous que le texte ne dépasse pas et se coupe avec des points de suspension
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: theme.spacing(1),
        },
        // S'assurer que le label ne déborde pas
        '& .MuiInputLabel-outlined': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 24px)',
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75) !important',
        },
    },
    actionIconsContainer: {
        display: 'flex',
        flexShrink: 0,
    },
    selectedItemsBox: {
        // Increase maxHeight to show one more line
        maxHeight: 110,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[50],
        width: '100%',
        boxSizing: 'border-box',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[100],
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.grey[600],
        },
    },
    selectedItemText: {
        cursor: 'pointer',
        padding: theme.spacing(0.25, 0.5),
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // Gérer le débordement du texte dans les éléments sélectionnés
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            textDecoration: 'none',
        },
    },
    noSelectionText: {
        padding: theme.spacing(1),
    }
}));

const FilterSelect = ({ options = [], label = 'Label', labelId = 'id_stade', labelDescription = 'description', onChange, value, ...props }) => {
    const classes = useStyles();
    const [selected, setSelected] = useState([]);

    useEffect(() => {
        if (value === '' && selected.length > 0) {
            setSelected([]);
        }
    }, [value, selected.length]);

    const handleChange = (event, newValue) => {
        setSelected(newValue);
        onChange(getListForSqlIn(newValue, labelId), label);
    };

    const handleRemoveItem = id => {
        const newSelected = selected.filter(item => item[labelId] !== id);
        setSelected(newSelected);
        onChange(getListForSqlIn(newSelected, labelId), label);
    };

    const reset = () => {
        setSelected([]);
        onChange(getListForSqlIn([], labelId), label);
    };

    const selectAll = () => {
        setSelected(options);
        onChange(getListForSqlIn(options, labelId), label);
    };

    return (
        <Box className={classes.filterSelectContainer} {...props}>
            <Box className={classes.autocompleteRow}>
                <div className={classes.autocompleteWrapper}>
                    <Autocomplete
                        multiple
                        value={selected}
                        options={options}
                        getOptionLabel={(option) => option[labelDescription] || ''}
                        onChange={handleChange}
                        disableCloseOnSelect
                        autoHighlight
                        autoComplete
                        clearOnEscape
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                variant={props.variant || "outlined"}
                                margin={props.margin || "dense"}
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                }}
                            />
                        )}
                        renderTags={() => null} // On masque les tags natifs pour garder l'affichage custom en dessous
                    />
                </div>
                {/* Icônes d'action à côté de l'Autocomplete */}
                <Box className={classes.actionIconsContainer}>
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={selectAll}
                        aria-label="Sélectionner tout"
                        title="Sélectionner tout"
                    >
                        <SelectAllRoundedIcon fontSize="small" />
                    </IconButton>
                    {selected.length !== 0 && (
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={reset}
                            aria-label="Effacer la sélection"
                            title="Effacer la sélection"
                        >
                            <ClearAllRoundedIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Conteneur de la liste des éléments sélectionnés */}
            <Box className={classes.selectedItemsBox}>
                {selected.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" align="center" className={classes.noSelectionText}>
                        Aucune sélection
                    </Typography>
                ) : (
                    selected.map((item, i) => (
                        <Typography
                            variant="body2"
                            align="left"
                            key={item[labelId] || i}
                            className={classes.selectedItemText}
                        >
                            {item[labelDescription]}
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(item[labelId])}
                                aria-label={`Supprimer ${item[labelDescription]}`}
                                title={`Supprimer ${item[labelDescription]}`}
                                style={{ padding: 2 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Typography>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default FilterSelect;